// A file's open document requests, on its Documents tab (Documents tab
// PR 6a, docs/design/documents-tab-workflow.md).
//
// CONSUMER: internal-ui (DocumentsTab, record layout).
//
// One row per open request: who it went to, how many boxes are filled,
// when it closes, and the two sender controls. "Nudge {first}" rings their
// bell again; "Revoke" withdraws the request (anything already uploaded
// stays filed on the record). Filled, revoked and expired requests drop
// off this list; the files they brought in are on the shelves.
//
// Nothing renders when the file has no open requests, so a file that never
// uses requests looks exactly as it did.

import { useCallback, useEffect, useState } from "react";

import { apiV11, type DocumentRequestRec, type RecordChecklistKind } from "../../api";
import { useToast } from "../Toast";
import { formatDate } from "../../utils/formatters";

function firstNameOf(name: string | null): string {
  if (!name) return "them";
  return name.trim().split(/\s+/)[0] ?? name;
}

export function RecordRequestsList({
  kind,
  recordId,
  refreshKey = 0,
}: {
  kind: RecordChecklistKind;
  recordId: string;
  /** Bump to reload (after a new request is sent, or a file lands). */
  refreshKey?: number;
}) {
  const toast = useToast();
  const [requests, setRequests] = useState<DocumentRequestRec[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmRevokeId, setConfirmRevokeId] = useState<string | null>(null);

  const load = useCallback(() => {
    return apiV11
      .listRecordRequests(kind, recordId)
      .then((resp) => setRequests(resp.requests))
      .catch(() => {
        // Best-effort: a failed load must never break the Documents card.
        setRequests([]);
      });
  }, [kind, recordId]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  if (requests.length === 0) return null;

  async function nudge(r: DocumentRequestRec) {
    setBusyId(r.id);
    try {
      await apiV11.nudgeDocumentRequest(r.id);
      toast.success(`Nudged ${firstNameOf(r.recipient_name)}.`);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not nudge.");
    } finally {
      setBusyId(null);
    }
  }

  async function newLink(r: DocumentRequestRec) {
    setBusyId(r.id);
    try {
      const { link_path } = await apiV11.reissueRequestLink(r.id);
      const url = `${window.location.origin}${link_path}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success("New link copied. The old one no longer works.");
      } catch {
        // No clipboard (an older browser, or a denied permission): show it.
        window.prompt("Copy the new link. The old one no longer works.", url);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not make a new link.");
    } finally {
      setBusyId(null);
    }
  }

  async function revoke(r: DocumentRequestRec) {
    setBusyId(r.id);
    try {
      await apiV11.revokeDocumentRequest(r.id);
      toast.success("Request withdrawn. Anything already uploaded stays on this file.");
      setConfirmRevokeId(null);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not revoke.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className="req-open" aria-label="Open requests">
      <div className="doc-card-sec">
        Requests out <small>waiting on someone</small>
      </div>
      <ul className="req-open-list">
        {requests.map((r) => {
          const first = firstNameOf(r.recipient_name);
          const waiting = r.slots.filter((s) => !s.document_id).map((s) => s.label);
          return (
            <li key={r.id} className="req-open-row" data-testid="record-request-row">
              <div className="req-open-main">
                <span className="req-open-name">
                  {r.recipient_email ?? r.recipient_name ?? "A teammate"} · {r.filled_count} of{" "}
                  {r.slots.length} in{r.recipient_email ? " · no-login link" : ""}
                </span>
                <span className="req-open-meta">
                  Waiting on {waiting.join(" · ")} · open until {formatDate(r.expires_at)}
                  {r.last_nudged_at ? ` · nudged ${formatDate(r.last_nudged_at)}` : ""}
                </span>
              </div>
              {confirmRevokeId === r.id ? (
                <span className="req-open-acts">
                  <span className="req-open-q">Withdraw this request?</span>
                  <button
                    type="button"
                    className="doc-link-btn doc-link-btn--danger"
                    disabled={busyId === r.id}
                    onClick={() => void revoke(r)}
                  >
                    Revoke
                  </button>
                  <button
                    type="button"
                    className="doc-link-btn"
                    onClick={() => setConfirmRevokeId(null)}
                  >
                    Keep it
                  </button>
                </span>
              ) : (
                <span className="req-open-acts">
                  {r.recipient_email ? (
                    // An outsider has no bell; the OS keeps only the link's
                    // hash, so "again" means a new link (the old one stops).
                    <button
                      type="button"
                      className="doc-btn"
                      disabled={busyId === r.id}
                      onClick={() => void newLink(r)}
                    >
                      Copy a new link
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="doc-btn"
                      disabled={busyId === r.id}
                      onClick={() => void nudge(r)}
                    >
                      Nudge {first}
                    </button>
                  )}
                  <button
                    type="button"
                    className="doc-link-btn doc-link-btn--danger"
                    disabled={busyId === r.id}
                    onClick={() => setConfirmRevokeId(r.id)}
                  >
                    Revoke
                  </button>
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
